import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import {
  BookOpen,
  CheckCircle2,
  Clock,
  GraduationCap,
  ExternalLink,
  Check,
  PlayCircle,
} from "lucide-react";
import { useToast } from "../../../components/admin/ToastContext";
import { get, post } from "../../../services/lewooApi";
import { PageSkeleton } from "../../../components/admin/LoadingSkeleton";
import { Modal } from "../../../components/admin/EssUi";

const ESSTrainingPage = () => {
  const intl = useIntl();
  const addToast = useToast();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({});
  const [slidesByChannel, setSlidesByChannel] = useState({});
  const [activeChannel, setActiveChannel] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [channelsRes, progressRes] = await Promise.all([
          get("/api/slide-channels"),
          get("/api/training-progress").catch(() => ({ data: [] })),
        ]);
        const channels = channelsRes?.data || [];
        setCourses(channels || []);

        const prog = {};
        (progressRes?.data || []).forEach((p) => {
          if (!p || p.channel_id == null || p.slide_id == null || p.completed === false) return;
          if (!prog[p.channel_id]) prog[p.channel_id] = new Set();
          prog[p.channel_id].add(p.slide_id);
        });
        setProgress(prog);

        const publishedChannels = (channels || []).filter((c) => c.website_published);
        const slideResults = await Promise.all(
          publishedChannels.map((c) => get(`/api/slide-channels/${c.id}/slides`).catch(() => ({ data: [] })))
        );
        const map = {};
        publishedChannels.forEach((c, i) => { map[c.id] = slideResults[i]?.data || []; });
        setSlidesByChannel(map);
      } catch (_) {
        setCourses([]);
        setProgress({});
      }
      setLoaded(true);
    };
    load();
  }, []);

  const published = courses.filter((c) => c.website_published);

  const completedCount = Object.values(progress).reduce((s, set) => s + set.size, 0);
  const totalMinutes = Object.entries(progress).reduce((m, [cid, set]) => {
    (slidesByChannel[cid] || []).forEach((s) => {
      if (set.has(s.id ?? s.lewoo_uid)) m += Number(s.completion_time) || 0;
    });
    return m;
  }, 0);
  const totalHours = totalMinutes > 0 ? `${(totalMinutes / 60).toFixed(1)}h` : "—";

  if (!loaded) return <PageSkeleton />;

  const stats = [
    { labelId: "ess.training.availableCourses", value: String(published.length), icon: BookOpen, color: "text-blue-600 bg-blue-100" },
    { labelId: "ess.training.completed", value: String(completedCount), icon: CheckCircle2, color: "text-green-600 bg-green-100" },
    { labelId: "ess.training.totalHours", value: totalHours, icon: Clock, color: "text-emerald-600 bg-emerald-100" },
    { labelId: "ess.training.certifications", value: "—", icon: GraduationCap, color: "text-amber-600 bg-amber-100" },
  ];

  const openCourse = (channel) => setActiveChannel(channel);

  const markComplete = async (slide) => {
    if (!activeChannel) return;
    const slideId = slide.id ?? slide.lewoo_uid;
    if (slideId == null) return;
    const cid = activeChannel.id;
    const done = progress[cid]?.has(slideId) || false;
    try {
      await post("/api/training-progress", { channel_id: cid, slide_id: slideId, completed: !done });
      setProgress((prev) => {
        const next = { ...prev };
        if (!next[cid]) next[cid] = new Set();
        if (!done) next[cid].add(slideId);
        else next[cid].delete(slideId);
        return next;
      });
      addToast(
        intl.formatMessage({ id: done ? "ess.training.markedIncomplete" : "ess.training.markedComplete" }),
        "success"
      );
    } catch (_) {
      addToast(intl.formatMessage({ id: "ess.common.submitError" }), "error");
    }
  };

  const activeSlides = activeChannel ? slidesByChannel[activeChannel.id] || [] : [];
  const activeDone = activeChannel ? progress[activeChannel.id]?.size || 0 : 0;

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {intl.formatMessage({ id: "ess.training.title" })}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {intl.formatMessage({ id: "ess.training.subtitle" })}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ labelId, value, icon: Icon, color }) => (
          <div key={labelId} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: labelId })}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                <Icon size={20} className={color.split(' ')[0]} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <h3 className="text-base font-bold text-gray-900">
        {intl.formatMessage({ id: "ess.training.availableCourses" })}
      </h3>
      {published.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center text-gray-400">
          {intl.formatMessage({ id: "ess.training.empty" })}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {published.map((course) => {
            const slides = slidesByChannel[course.id] || [];
            const done = progress[course.id]?.size || 0;
            const pct = slides.length > 0 ? Math.round((done / slides.length) * 100) : 0;
            return (
              <div key={course.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 transition-all hover:shadow-md flex flex-col justify-between">
                <div>
                  <h4 className="text-sm font-bold text-gray-900">{course.name}</h4>
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                    {course.description || intl.formatMessage({ id: "ess.training.defaultDescription" })}
                  </p>
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-500">{intl.formatMessage({ id: "ess.training.courseProgress" })}</span>
                      <span className="text-xs font-semibold text-gray-700">{pct}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${pct >= 100 ? "bg-green-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{intl.formatMessage({ id: "ess.training.slidesCount" }, { count: slides.length })}</p>
                  </div>
                </div>
                <button
                  onClick={() => openCourse(course)}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-colors"
                >
                  {intl.formatMessage({ id: "ess.training.accessCourse" })} <PlayCircle size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={!!activeChannel}
        onClose={() => setActiveChannel(null)}
        title={activeChannel ? activeChannel.name : ""}
        size="lg"
      >
        {activeChannel && (
          <>
            {activeChannel.description && (
              <p className="text-sm text-gray-500 mb-4">{activeChannel.description}</p>
            )}
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-gray-700">
                {intl.formatMessage({ id: "ess.training.courseProgress" })} — {activeDone}/{activeSlides.length}
              </span>
              <span className="text-xs font-semibold text-gray-700">
                {activeSlides.length > 0 ? Math.round((activeDone / activeSlides.length) * 100) : 0}%
              </span>
            </div>
            {activeSlides.length === 0 ? (
              <div className="py-10 text-center text-gray-400">{intl.formatMessage({ id: "ess.training.noSlides" })}</div>
            ) : (
              <div className="space-y-4">
                {activeSlides.map((slide, i) => {
                  const slideId = slide.id ?? slide.lewoo_uid;
                  const isDone = slideId != null && (progress[activeChannel.id]?.has(slideId) || false);
                  return (
                    <div key={slideId ?? i} className={`border rounded-xl p-4 transition-colors ${isDone ? "border-green-200 bg-green-50/50" : "border-gray-200 bg-white"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold bg-gray-100 text-gray-600 flex-shrink-0">
                            {i + 1}
                          </span>
                          <h5 className="text-sm font-semibold text-gray-900 truncate">{slide.name || `Slide ${i + 1}`}</h5>
                        </div>
                        {isDone && <Check size={18} className="text-green-600 flex-shrink-0" />}
                      </div>
                      {slide.description && (
                        <p className="text-xs text-gray-500 mt-2 ml-8 whitespace-pre-wrap">{slide.description}</p>
                      )}
                      {slide.content && (
                        <div className="text-xs text-gray-600 mt-2 ml-8 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">
                          {slide.content}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-3 ml-8">
                        {slide.url && (
                          <a
                            href={slide.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-800 transition-colors"
                          >
                            {intl.formatMessage({ id: "ess.training.openLink" })} <ExternalLink size={12} />
                          </a>
                        )}
                        <button
                          onClick={() => markComplete(slide)}
                          disabled={slideId == null}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 ${
                            isDone
                              ? "bg-green-100 text-green-700 hover:bg-green-200"
                              : "bg-emerald-500 text-white hover:bg-emerald-600"
                          }`}
                        >
                          {isDone ? <CheckCircle2 size={14} /> : <Check size={14} />}
                          {isDone
                            ? intl.formatMessage({ id: "ess.training.complete" })
                            : intl.formatMessage({ id: "ess.training.markComplete" })}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default ESSTrainingPage;
